import { useContext, useEffect, useState } from "react";
import { Avatar, Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import { HiOutlineArrowPath } from "react-icons/hi2";
import { HiLockClosed } from "react-icons/hi2";
import toast from "react-hot-toast";

import { useModal } from "../../ReusableComponents/Modal";
import { Context } from "../../../../Store/Store";
import { useGlobalContext } from "../../../../Context/GlobalContext";
import {
  GetTonesService,
  GenerateLinkedInCommentService,
  SubmitLinkedInCommentService,
  parseGeneratedComment,
} from "../../../../Services/LinkedInCommentService";
import { mixpanelEvent } from "../../../../Utils/mixpanelUtils";

// Visual language matches the Chrome extension's "Engage" tab (sidepanel.css: .sp-engage-*).
const styles = {
  Modal: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    fontFamily: "Inter, sans-serif",
    "& h2": {
      fontSize: "18px",
      fontWeight: "700",
      color: "#111",
      margin: "0",
    },
  },
  divider: {
    height: "1px",
    background: "#e5e5e5",
    margin: "4px 0",
  },
  postCard: {
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "12px",
    background: "#f3f4f6",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  postCardRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  postAuthor: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#111",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  postText: {
    fontSize: "12px",
    color: "#6b7280",
    whiteSpace: "pre-wrap",
    maxHeight: "160px",
    overflowY: "auto",
  },
  tonesLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#111",
  },
  toneGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
    gap: "8px",
  },
  toneBtn: {
    padding: "8px 10px",
    border: "1.5px solid #d1d5db",
    borderRadius: "8px",
    background: "#fff",
    fontSize: "13px",
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    transition: "border-color 0.15s, background 0.15s, color 0.15s",
    color: "#111",
    "&:hover:not(:disabled)": {
      background: "#f3f4f6",
      borderColor: "#9ca3af",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  toneBtnSelected: {
    background: "#000",
    color: "#fff",
    borderColor: "#000",
    "&:hover:not(:disabled)": {
      background: "#000",
      borderColor: "#000",
    },
  },
  lockedNote: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "11px",
    color: "#6b7280",
    margin: "4px 0 0",
  },
  lockedLink: {
    background: "none",
    border: "none",
    padding: "0",
    font: "inherit",
    fontSize: "11px",
    color: "#2563eb",
    cursor: "pointer",
    textDecoration: "underline",
  },
  resultFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  wordCount: {
    fontSize: "12px",
    color: "#9ca3af",
  },
  retryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    background: "#fff",
    color: "#374151",
    border: "1.5px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "13px",
    cursor: "pointer",
    "&:hover:not(:disabled)": {
      borderColor: "#6b7280",
      background: "#f3f4f6",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  postToLinkedInBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    "&:hover:not(:disabled)": {
      background: "#222",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  formErr: {
    fontSize: "13px",
    fontWeight: "500",
    padding: "8px 16px",
    color: "var(--color-red)",
    background: "var(--color-red-100)",
    borderRadius: "8px",
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 0",
  },
  textArea: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f9fafb",
      borderRadius: "8px",
      fontSize: "13px",
      "& fieldset": { borderColor: "#e0e2e4" },
    },
  },
};

function getInitials(name) {
  return (
    name
      ?.split(" ")
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?"
  );
}

function GenerateCommentModal({ postData, onCloseModal }) {
  const [state] = useContext(Context);
  const { isPayingUser } = useGlobalContext();
  const { open: openModalWindow } = useModal();
  const email = state?.user?.data?.email;

  const [step, setStep] = useState("loadingTones"); // loadingTones | pickTone | generating | review | submitting
  const [tones, setTones] = useState([]);
  const [trialEndedMessage, setTrialEndedMessage] = useState("");
  const [selectedTone, setSelectedTone] = useState(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  const {
    "post link": postLink = "",
    "post summary": postSummary = "",
    post_content: postContent = "",
    id: postId,
    first_name: firstName = "",
    last_name: lastName = "",
    profile_photo_url: profilePhotoUrl,
  } = postData ?? {};
  // post_content is the actual scraped LinkedIn post text; post_summary is a separate
  // AI-generated summary of it (shorter, sometimes drifts from the original wording) -
  // generation and the preview here should reflect the real post, not the summary.
  const fullPostText = postContent.trim() || postSummary;
  const authorName = `${firstName} ${lastName}`.trim();
  const wordCount = comment.trim() ? comment.trim().split(/\s+/).filter(Boolean).length : 0;

  useEffect(() => {
    if (!email) return;
    GetTonesService(email)
      .then((res) => res.json())
      .then((data) => {
        if (data?.trial_ended_message) {
          setTrialEndedMessage(data.trial_ended_message);
          setStep("trialEnded");
          return;
        }
        setTones(data?.savedTones ?? []);
        setStep("pickTone");
      })
      .catch(() => {
        setError("Could not load tones. Please try again.");
        setStep("pickTone");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handlePickTone = async (tone) => {
    setSelectedTone(tone);
    setError("");
    setStep("generating");
    mixpanelEvent("ai_comment_tone_selected", {
      post_id: postId,
      tone: tone.label,
      screen_name: "posting activity by monitored prospects",
    });
    try {
      const res = await GenerateLinkedInCommentService({
        post_url: postLink,
        post: fullPostText,
        prompt: tone.prompt,
        word_limit: tone.toneCharacterLength,
        current_plan: isPayingUser ? "paying" : "trial",
        email,
      });
      const rawText = await res.text();
      if (!res.ok) throw new Error("generation failed");
      setComment(parseGeneratedComment(rawText));
      setStep("review");
    } catch {
      setError("Could not generate a comment. Please try again.");
      setStep("pickTone");
    }
  };

  const handleSubmit = async () => {
    setError("");
    setStep("submitting");
    try {
      const res = await SubmitLinkedInCommentService({ post_url: postLink, comment, post_id: postId });
      if (res.ok) {
        mixpanelEvent("ai_comment_posted", {
          post_id: postId,
          post_url: postLink,
          screen_name: "posting activity by monitored prospects",
        });
        toast.success("Comment posted to LinkedIn");
        onCloseModal();
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (body?.error === "cookie_missing" || body?.error === "cookie_expired") {
        openModalWindow("cookieErr");
        return;
      }
      setError(body?.message || "Failed to submit comment to LinkedIn. Please try again.");
      setStep("review");
    } catch {
      setError("Network error. Please try again.");
      setStep("review");
    }
  };

  const anyLocked = tones.some((t) => t.islocked);

  return (
    <Box sx={styles.Modal}>
      <h2 style={{ marginTop: "6px", marginBottom: "10px" }}>Generate a comment</h2>

      <Box sx={styles.postCard}>
        <Box sx={styles.postCardRow}>
          <Avatar sx={{ width: 34, height: 34, fontSize: "13px", bgcolor: "#6366f1" }} src={profilePhotoUrl}>
            {getInitials(authorName)}
          </Avatar>
          <Typography sx={styles.postAuthor}>{authorName || "Unknown"}</Typography>
        </Box>
        <Typography sx={styles.postText}>{fullPostText}</Typography>
      </Box>

      {!!error && <Typography sx={styles.formErr}>{error}</Typography>}

      {step === "loadingTones" && (
        <Box sx={styles.centered}>
          <CircularProgress size={28} sx={{ color: "#000" }} thickness={4} />
        </Box>
      )}

      {step === "trialEnded" && <Typography sx={{ fontSize: "13px", color: "#7f1d1d" }}>{trialEndedMessage}</Typography>}

      {step !== "loadingTones" && step !== "trialEnded" && tones.length > 0 && (
        <>
          <Box sx={styles.divider} />
          <Typography sx={styles.tonesLabel}>Choose your tone</Typography>
          <Box sx={styles.toneGrid}>
            {tones.map((tone) => (
              <Box
                key={tone.label}
                component="button"
                sx={{ ...styles.toneBtn, ...(selectedTone?.label === tone.label ? styles.toneBtnSelected : {}) }}
                disabled={!!tone.islocked || step === "generating" || step === "submitting"}
                onClick={() => handlePickTone(tone)}
              >
                {!!tone.islocked && <HiLockClosed size={11} />}
                {tone.label}
              </Box>
            ))}
          </Box>
          {anyLocked && (
            <Typography sx={styles.lockedNote} component="p">
              <HiLockClosed size={11} />
              Locked tones require a paid plan.{" "}
              <Box
                component="a"
                href="https://apps.kynzo.io/Billing"
                target="_blank"
                rel="noreferrer"
                sx={styles.lockedLink}
              >
                Upgrade
              </Box>
            </Typography>
          )}

          <Box sx={styles.divider} />

          <TextField
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            sx={styles.textArea}
            placeholder={step === "generating" ? "Writing your comment…" : "Pick a tone above, or write your own comment here"}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={step === "generating" || step === "submitting"}
            InputProps={
              step === "generating"
                ? {
                    endAdornment: (
                      <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                        <CircularProgress size={16} thickness={5} sx={{ color: "#000" }} />
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />

          <Box sx={styles.resultFooter}>
            <Typography sx={styles.wordCount}>{wordCount} words</Typography>
            <Box display="flex" gap="8px">
              {selectedTone && (
                <Box
                  component="button"
                  sx={styles.retryBtn}
                  disabled={step === "generating" || step === "submitting"}
                  onClick={() => handlePickTone(selectedTone)}
                >
                  <HiOutlineArrowPath size={12} />
                  Retry
                </Box>
              )}
              <Box
                component="button"
                sx={styles.postToLinkedInBtn}
                disabled={step === "generating" || step === "submitting" || !comment.trim()}
                onClick={handleSubmit}
              >
                {step === "submitting" && <CircularProgress size={12} sx={{ color: "#fff" }} />}
                Post to LinkedIn
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default GenerateCommentModal;
